import { Building2, Plus, Star, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { ClientFormDialog } from '@/pages/admin/clients/ClientFormDialog';
import { ImportClientsDialog } from '@/pages/admin/clients/ImportClientsDialog';
import { useClients } from '@/hooks/clients';
import { formatDay } from '@/lib/dates';
import type { ClientView } from '@shared/schemas/client';
import {
  ACTIVITY_FILTER_OPTIONS,
  DEFAULT_PAGE_SIZE,
  PEOPLE_SORTS,
} from '@shared/schemas/pagination';
import type { ActivityFilter } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { PeopleSort } from '@shared/schemas/pagination';

/*
 * /admin/clients — ROADMAP.md §3.5.
 *
 * `business_name` leads when there is one. The prototype's "Property Manager" is
 * a company, and a list that renders every client as "first last" turns a
 * company into a person nobody can find by name.
 *
 * The address column comes from the client's **primary property**, never from
 * the client record — there is no address on `client`, which is the fix for P14
 * (the prototype has two that disagree).
 */

export function ClientsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<ActivityFilter>('active');
  const [highPriorityOnly, setHighPriorityOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<PeopleSort>('name');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const clients = useClients({ sort, page, search, activity, highPriorityOnly });

  const columns: Column<ClientView>[] = [
    {
      key: 'name',
      header: 'Client',
      hideLabelOnCard: true,
      render: (row) => (
        <div className="min-w-0">
          <span className="flex items-center gap-1.5 text-body font-medium text-ink-900">
            {row.isHighPriority ? (
              // The prototype's star. It is not decoration — high priority is
              // what gates calendar sync (business-rules.md §6).
              <Star
                aria-hidden="true"
                className="size-4 shrink-0 fill-honey-400 text-status-pending-text"
              />
            ) : null}
            {/* A real link, not just a clickable row — see JobsPage for why.
                `onRowClick` alone left this screen mouse-only (WCAG 2.1.1). */}
            <Link to={`/admin/clients/${row.id}`} className="underline underline-offset-2">
              {row.displayName}
            </Link>
            {row.isHighPriority ? <span className="sr-only"> (high priority)</span> : null}
          </span>
          {row.businessName ? (
            <span className="mt-0.5 block text-meta text-ink-500">{row.contactName}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (row) => (
        <div className="min-w-0 text-body">
          {row.email ? <span className="block truncate">{row.email}</span> : null}
          {row.phone ? <span className="block text-meta text-ink-500">{row.phone}</span> : null}
        </div>
      ),
    },
    {
      key: 'address',
      header: 'Primary address',
      render: (row) =>
        row.primaryAddress ? (
          <span className="text-body">{row.primaryAddress}</span>
        ) : (
          // Not a dash. "No property yet" is a fact and an action; a dash is
          // neither (P1, P10).
          <span className="text-meta text-ink-500">No property yet</span>
        ),
    },
    {
      key: 'properties',
      header: 'Properties',
      render: (row) => <span className="text-body">{row.propertyCount}</span>,
    },
    {
      key: 'since',
      header: 'Customer since',
      render: (row) =>
        row.customerSince ? (
          <span className="whitespace-nowrap text-body">{formatDay(row.customerSince)}</span>
        ) : (
          <span className="text-meta text-ink-500">Not recorded</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.isActive ? (
          <span className="text-meta text-ink-500">Active</span>
        ) : (
          <StatusBadge label="Archived" tone="cancelled" />
        ),
    },
  ];

  return (
    <AdminPage
      title="Clients"
      description="Homeowners and property managers, their properties, and how to get in."
      actions={
        <div className="flex flex-wrap gap-2">
          {/* Secondary to Add client — importing is a once-or-twice thing and
              adding somebody is the daily one. */}
          <Button
            variant="outline"
            onClick={() => {
              setImporting(true);
            }}
          >
            <Upload aria-hidden="true" />
            Import
          </Button>
          <Button
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus aria-hidden="true" />
            Add client
          </Button>
        </div>
      }
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            label="Search clients"
            placeholder="Name, business, email or phone"
            className="sm:w-72"
          />

          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="highPriorityOnly"
              checked={highPriorityOnly}
              onChange={(event) => {
                setHighPriorityOnly(event.target.checked);
                setPage(1);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="whitespace-nowrap text-meta text-ink-700">High priority only</span>
          </label>
        </div>

        <FilterPills
          label="Filter clients by status"
          options={ACTIVITY_FILTER_OPTIONS}
          value={activity}
          onChange={(next) => {
            setActivity(next);
            setPage(1);
          }}
        />
      </div>

      {/* The order control. A select rather than clickable headers —

          the list becomes cards below 768px and a card has no headers. */}

      <div className="mb-3 flex justify-end">
        <SortSelect
          label="Sort clients"

          options={PEOPLE_SORTS}

          value={sort}

          onChange={(next) => {
            setSort(next);

            // Page 1, or you are on page 3 of a differently ordered list.

            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Clients, how to reach them, and their primary address"
        columns={columns}
        rows={clients.data?.data ?? []}
        rowKey={(row) => row.id}
        // On a phone the whole card is the link — see DataTable's `rowHref`.
        rowHref={(row) => `/admin/clients/${row.id}`}
        isLoading={clients.isPending}
        error={clients.error}
        onRetry={() => {
          void clients.refetch();
        }}
        onRowClick={(row) => {
          void navigate(`/admin/clients/${row.id}`);
        }}
        empty={
          <EmptyState
            icon={Building2}
            title={
              search || highPriorityOnly ? 'No clients match those filters.' : 'No clients yet.'
            }
            hint={
              search || highPriorityOnly
                ? 'Try clearing the search or the priority filter.'
                : 'A client is a homeowner or a property manager. Their addresses live on their properties, which you add after creating them.'
            }
            action={
              search || highPriorityOnly ? null : (
                <Button
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <Plus aria-hidden="true" />
                  Add client
                </Button>
              )
            }
          />
        }
      />

      {clients.data ? (
        <Pagination
          page={clients.data.page}
          pageSize={clients.data.pageSize || DEFAULT_PAGE_SIZE}
          total={clients.data.total}
          onPageChange={setPage}
          itemNoun="clients"
        />
      ) : null}

      <ImportClientsDialog open={importing} onOpenChange={setImporting} />

      <ClientFormDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(created) => {
          // Straight to the detail page, because the next thing anybody does
          // after creating a client is add the property they are cleaning.
          void navigate(`/admin/clients/${created.id}`);
        }}
      />
    </AdminPage>
  );
}
